import React from "react";
import { cn, type ClassValue } from "../lib/cn";

export type InputVariant = "default" | "search";

export type InputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size" | "color"
> & {
  variant?: InputVariant;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  containerClassName?: string;
  className?: string;
};

/**
 * On-brand input primitive:
 * - Matches `.kkp-input` styling from the login page
 * - Adds optional left/right icons (useful for search)
 * - Keeps styling token-driven via CSS variables in `brand-base.css`
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  function Input(
    {
      variant = "default",
      leftIcon,
      rightIcon,
      containerClassName,
      className,
      disabled,
      type = "text",
      ...props
    },
    ref,
  ) {
    const base: ClassValue = [
      "w-full",
      "px-3 py-2.5",
      "rounded-xl",
      "border",
      "border-[color:rgba(255,255,255,0.12)]",
      "bg-[color:rgba(255,255,255,0.06)]",
      "text-[color:rgba(255,255,255,0.92)]",
      "placeholder:text-[color:rgba(255,255,255,0.35)]",
      "outline-none",
      "transition",
      "focus:border-[color:color-mix(in_oklab,var(--kkp-magenta)_70%,white)]",
      "focus:shadow-[0_0_0_3px_rgba(203,72,226,0.18)]",
      "disabled:cursor-not-allowed disabled:opacity-60",
    ];

    const withIconPadding: ClassValue = [
      leftIcon ? "pl-10" : null,
      rightIcon ? "pr-10" : null,
    ];

    const variantClasses: Record<InputVariant, ClassValue> = {
      default: null,
      search: "pr-10",
    };

    return (
      <div className={cn("relative", containerClassName)}>
        {leftIcon ? (
          <span
            className={cn(
              "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2",
              "text-[color:var(--kkp-text-muted)]",
            )}
            aria-hidden="true"
          >
            {leftIcon}
          </span>
        ) : null}

        <input
          ref={ref}
          type={type}
          disabled={disabled}
          className={cn(
            base,
            withIconPadding,
            variantClasses[variant],
            className,
          )}
          {...props}
        />

        {rightIcon ? (
          <span
            className={cn(
              "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2",
              "text-[color:var(--kkp-text-muted)]",
            )}
            aria-hidden="true"
          >
            {rightIcon}
          </span>
        ) : null}
      </div>
    );
  },
);

export type LabelProps = React.LabelHTMLAttributes<HTMLLabelElement> & {
  muted?: boolean;
};

/**
 * Label primitive matching the login page label style (`.kkp-label`).
 */
export function Label({ className, muted = true, ...props }: LabelProps) {
  return (
    <label
      className={cn(
        "flex flex-col gap-1.5 text-xs",
        muted
          ? "text-[color:color-mix(in_oklab,var(--kkp-text-muted)_92%,white)]"
          : "text-[color:rgba(255,255,255,0.92)]",
        className,
      )}
      {...props}
    />
  );
}

export type FieldProps = {
  label: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
};

/**
 * Field wrapper for consistent spacing + hint/error text.
 * Keep it small and predictable (BLUEPRINT friendly).
 */
export function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
}: FieldProps) {
  const showError = Boolean(error);

  return (
    <div className={cn("grid gap-1.5", className)}>
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-[color:rgba(255,255,255,0.9)]">
              {label}
            </span>
            {required ? (
              <span className="text-xs text-[color:var(--kkp-text-muted)]">
                *
              </span>
            ) : null}
          </div>
          {hint ? (
            <div className="mt-0.5 text-[11px] text-[color:var(--kkp-text-muted)]">
              {hint}
            </div>
          ) : null}
        </div>

        {showError ? (
          <div className="text-[11px] font-semibold text-[color:rgba(239,68,68,0.95)]">
            {error}
          </div>
        ) : null}
      </div>

      {children}
    </div>
  );
}
